import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

export interface QuarterlyReport {
  id?: number;
  organizationId: number;
  year: number;
  quarter: number;
  reportText: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface PeriodData {
  year: number;
  reportType: 'month' | 'quarter' | 'half-year' | 'year';
  quarter: number;
  month: number;
  halfYear: number;
}

export interface CreateQuarterlyReportRequest {
  organization_id: number;
  year: number;
  quarter?: number;
  report_text: string;
  reportType?: 'month' | 'quarter' | 'half-year' | 'year' | 'comparative';
  month?: number;
  halfYear?: number;
  // Comparative report specific fields
  comparativePeriod1?: PeriodData;
  comparativePeriod2?: PeriodData;
}

export interface UpdateQuarterlyReportRequest {
  report_text: string;
}

class QuarterlyReportService {
  private getAuthHeaders() {
    const token = localStorage.getItem('token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }

  async createQuarterlyReport(report: CreateQuarterlyReportRequest): Promise<{success: boolean, data?: QuarterlyReport, message: string}> {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/quarterly-reports`,
        report,
        { headers: this.getAuthHeaders() }
      );
      return response.data;
    } catch (error: any) {
      console.error('Error creating quarterly report:', error);
      if (error.response?.data) {
        return error.response.data;
      }
      return {
        success: false,
        message: 'Network error occurred while creating quarterly report'
      };
    }
  }

  async getQuarterlyReports(year?: number): Promise<QuarterlyReport[]> {
    try {
      const url = year 
        ? `${API_BASE_URL}/quarterly-reports?year=${year}`
        : `${API_BASE_URL}/quarterly-reports`;
        
      const response = await axios.get(
        url,
        { headers: this.getAuthHeaders() }
      );
      return response.data.success ? response.data.data : [];
    } catch (error: any) {
      console.error('Error fetching quarterly reports:', error);
      return [];
    }
  }

  async getQuarterlyReportById(id: number): Promise<QuarterlyReport | null> {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/quarterly-reports/${id}`,
        { headers: this.getAuthHeaders() }
      );
      return response.data.success ? response.data.data : null;
    } catch (error: any) {
      console.error('Error fetching quarterly report:', error);
      return null;
    }
  }

  async getQuarterlyReportByYearQuarter(year: number, quarter: number): Promise<QuarterlyReport | null> {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/quarterly-reports/year/${year}/quarter/${quarter}`,
        { headers: this.getAuthHeaders() }
      );
      return response.data.success ? response.data.data : null;
    } catch (error: any) {
      console.error('Error fetching quarterly report by year/quarter:', error);
      return null;
    }
  }

  async updateQuarterlyReport(id: number, updates: UpdateQuarterlyReportRequest): Promise<QuarterlyReport | null> {
    try {
      const response = await axios.put(
        `${API_BASE_URL}/quarterly-reports/${id}`,
        updates,
        { headers: this.getAuthHeaders() }
      );
      return response.data.success ? response.data.data : null;
    } catch (error: any) {
      console.error('Error updating quarterly report:', error);
      throw new Error(error.response?.data?.message || 'Failed to update quarterly report');
    }
  }

  async deleteQuarterlyReport(id: number): Promise<{success: boolean, message: string}> {
    try {
      const response = await axios.delete(
        `${API_BASE_URL}/quarterly-reports/${id}`,
        { headers: this.getAuthHeaders() }
      );
      return response.data;
    } catch (error: any) {
      console.error('Error deleting quarterly report:', error);
      if (error.response?.data) {
        return error.response.data;
      }
      return {
        success: false,
        message: 'Network error occurred while deleting quarterly report'
      };
    }
  }

  async getAllQuarterlyReports(): Promise<QuarterlyReport[]> {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/quarterly-reports/all`,
        { headers: this.getAuthHeaders() }
      );
      return response.data.success ? response.data.data : [];
    } catch (error: any) {
      console.error('Error fetching all quarterly reports:', error);
      return [];
    }
  }
}

const quarterlyReportService = new QuarterlyReportService();
export default quarterlyReportService;






